import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme, keyframes } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Navbar from './components/Navbar';
import Home from './pages/Home';
import People from './pages/people/People';
import Faculty from './pages/people/Faculty';
import Students from './pages/people/Students';
import Academics from './pages/Academics';
import Events from './pages/Events';
import News from './pages/News';
import About from './pages/About';
import Placement from './pages/Placement';
import Footer from './components/Footer';
import Loading from './components/Loading';
import AchievementsPage from './pages/AchievementsPage';
import Contact from './pages/Contact';
import Research from './pages/Research';
import Alumni from './pages/people/Alumni';

// Define keyframes for animations
const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const fadeInOut = keyframes`
  0% {
    opacity: 0.4;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.4;
  }
`;

const theme = createTheme({
  palette: {
    primary: {
      main: '#1F2937', // Dark gray-blue
      light: '#374151',
      dark: '#111827',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#4B5563', // Medium gray
      light: '#6B7280',
      dark: '#374151',
      contrastText: '#ffffff',
    },
    background: {
      default: '#F9FAFB', // Very light gray
      paper: '#ffffff',
    },
    text: {
      primary: '#1F2937',
      secondary: '#4B5563',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 600,
      fontSize: '2.5rem',
      lineHeight: 1.2,
      color: '#1F2937',
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
      lineHeight: 1.3,
      color: '#1F2937',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      lineHeight: 1.3,
      color: '#1F2937',
    },
    h4: {
      fontWeight: 500,
      fontSize: '1.5rem',
      lineHeight: 1.4,
      color: '#1F2937',
    },
    h5: {
      fontWeight: 500,
      fontSize: '1.25rem',
      lineHeight: 1.4,
      color: '#1F2937',
    },
    h6: {
      fontWeight: 500,
      fontSize: '1rem',
      lineHeight: 1.4,
      color: '#1F2937',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      color: '#1F2937',
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
      color: '#4B5563',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 4,
          padding: '8px 24px',
          fontWeight: 500,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 1px 2px rgba(0,0,0,0.05)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          boxShadow: '0px 1px 3px rgba(0,0,0,0.05)',
          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0px 2px 4px rgba(0,0,0,0.08)',
          },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: { xs: 2, sm: 3, md: 4 },
          paddingRight: { xs: 2, sm: 3, md: 4 },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1F2937', // Dark gray color
          color: '#ffffff',
          boxShadow: '0px 2px 10px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '@keyframes spin': {
          '0%': {
            transform: 'rotate(0deg)',
          },
          '100%': {
            transform: 'rotate(360deg)',
          },
        },
        '@keyframes fadeInOut': {
          '0%': {
            opacity: 0.4,
          },
          '50%': {
            opacity: 1,
          },
          '100%': {
            opacity: 0.4,
          },
        },
      },
    },
  },
  shape: {
    borderRadius: 4,
  },
  shadows: [
    'none',
    '0px 1px 2px rgba(0,0,0,0.05)',
    '0px 1px 3px rgba(0,0,0,0.05)',
    '0px 1px 4px rgba(0,0,0,0.05)',
    '0px 2px 4px rgba(0,0,0,0.05)',
    '0px 2px 5px rgba(0,0,0,0.05)',
    '0px 3px 6px rgba(0,0,0,0.05)',
    '0px 3px 7px rgba(0,0,0,0.05)',
    '0px 4px 8px rgba(0,0,0,0.05)',
    '0px 4px 9px rgba(0,0,0,0.05)',
    '0px 5px 10px rgba(0,0,0,0.05)',
    '0px 5px 11px rgba(0,0,0,0.05)',
    '0px 6px 12px rgba(0,0,0,0.05)',
    '0px 6px 13px rgba(0,0,0,0.05)',
    '0px 7px 14px rgba(0,0,0,0.05)',
    '0px 7px 15px rgba(0,0,0,0.05)',
    '0px 8px 16px rgba(0,0,0,0.05)',
    '0px 8px 17px rgba(0,0,0,0.05)',
    '0px 9px 18px rgba(0,0,0,0.05)',
    '0px 9px 19px rgba(0,0,0,0.05)',
    '0px 10px 20px rgba(0,0,0,0.05)',
    '0px 10px 21px rgba(0,0,0,0.05)',
    '0px 11px 22px rgba(0,0,0,0.05)',
    '0px 11px 23px rgba(0,0,0,0.05)',
    '0px 12px 24px rgba(0,0,0,0.05)',
  ],
});

function App() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate loading time
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2000); // Show loading for 2 seconds

    return () => clearTimeout(timer);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        {loading ? (
          <Loading />
        ) : (
          <>
            <Navbar />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/people" element={<People />} />
              <Route path="/people/faculty" element={<Faculty />} />
              <Route path="/people/students" element={<Students />} />
              <Route path="/people/alumni" element={<Alumni />} />
              <Route path="/academics" element={<Academics />} />
              <Route path="/events" element={<Events />} />
              <Route path="/placement" element={<Placement />} />
              <Route path="/news" element={<News />} />
              <Route path="/about" element={<About />} />
              <Route path="/achievements" element={<AchievementsPage />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/research" element={<Research />} />
            </Routes>
            <Footer />
          </>
        )}
      </Router>
    </ThemeProvider>
  );
}

export default App; 